import React, { createContext, useContext } from 'react';
import { PageProps } from 'gatsby';
import { Elements, RichTextBlock, RichTextProps, RichTextSpan } from 'prismic-reactjs';
import { GatsbyImageProps } from 'gatsby-plugin-image';

export const richTextWithPlaceholder = (
  existingText: { richText: RichTextBlock[] | null } | null,
  text: string,
  type: Elements = Elements.paragraph
): RichTextBlock[] =>
  existingText?.richText ?? [
    {
      type,
      text,
      spans: [],
    },
  ];

export const splitRichTextByNewLine = (baseText: RichTextBlock[]): RichTextBlock[][] =>
  baseText.reduce((acm, { text = '', type, spans }) => {
    const splitText = text.split('\n');

    const splitTextCharacterCounts = splitText.reduce((acm, text, index) => {
      const start = index ? acm[index - 1].end + '\n'.length : 0;
      const end = start + text.length;

      return [...acm, { start, end: end }];
    }, [] as { start: number; end: number }[]);

    const splitTextSpans: RichTextSpan[][] = splitTextCharacterCounts.map(({ start, end }) => {
      return (
        spans?.reduce((acm, span) => {
          const spanStart = span.start >= start ? span.start - start : 0;
          const spanEnd = span.end >= end ? end : span.end - start;

          if (spanStart >= 0 && spanEnd >= 0) {
            return [
              ...acm,
              {
                start: spanStart,
                end: spanEnd,
                type: span.type,
              },
            ];
          } else return acm;
        }, [] as RichTextSpan[]) ?? []
      );
    });

    return [
      ...acm,
      ...text.split('\n').map((textLine, index) => [
        {
          spans: splitTextSpans[index],
          text: textLine,
          type,
        },
      ]),
    ];
  }, []);

export const makeDataContext = <Query extends PageProps['data']>() => {
  const Context = createContext<Query | null>(null);
  const useDataContext = () => useContext(Context) as NonNullable<Query>;

  const withProvider = (Component: React.FC<Omit<PageProps, 'data'>>): React.FC<PageProps<Query>> =>
    function Provider({ data, children, ...props }) {
      return (
        <Context.Provider value={data}>
          <Component {...props}>{children}</Component>
        </Context.Provider>
      );
    };

  return [useDataContext, withProvider] as [typeof useDataContext, typeof withProvider];
};

type IContent = ({ key: string } & (
  | { type: CONTENT_BLOCK_TYPE.RICH_TEXT; props: RichTextProps }
  | { type: CONTENT_BLOCK_TYPE.IMAGE; props: GatsbyImageProps }
))[];

export const normalizeRichTextImageContent = (
  content: RichTextBlock[],
  helpers: {
    keyBuilder: (index: number) => string;
    altBuilder: (index: number) => string;
    placeholderColor: string;
  } = {
    keyBuilder: index => index.toString(),
    altBuilder: index => index.toString(),
    placeholderColor: 'transparent',
  }
): IContent => {
  const contentBlocks = content.reduce(
    (acm, contentBlock, index) => {
      if (contentBlock.type === Elements.image) {
        const trailingBlock = content.length - 1 === index ? [] : [[]];

        return [...acm, [contentBlock], ...trailingBlock];
      }

      const [lastContentBlock] = acm.slice(-1);

      return [...acm.slice(0, -1), [...lastContentBlock, contentBlock]];
    },
    [[]]
  );

  return contentBlocks.map((contentBlock, contentIndex) => {
    const key = helpers.keyBuilder(contentIndex);

    const [content] = contentBlock;

    if (content.type === Elements.image) {
      const { url, alt, dimensions } = content;

      return {
        key,
        type: CONTENT_BLOCK_TYPE.IMAGE,
        props: {
          alt: alt ?? helpers.altBuilder(contentIndex),
          backgroundColor: helpers.placeholderColor,
          image: {
            layout: 'fixed',
            width: dimensions?.width as number,
            height: dimensions?.height as number,
            images: {
              fallback: { src: url as string },
            },
          },
        },
      };
    }

    return {
      key,
      type: CONTENT_BLOCK_TYPE.RICH_TEXT,
      props: { render: contentBlock },
    };
  });
};

export enum CONTENT_BLOCK_TYPE {
  RICH_TEXT = 'rich_text',
  IMAGE = 'image',
}
